// Транспорт и хуки бытового блока портала 73: овертаймы, командировки, каршеринг.
// ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/p73.ts: там инженерный блок того же портала — работы по судам,
// открытые всем ролям CRM. Здесь заработок конкретных людей, и раздел закрыт (см. шапку
// p73x_api.py). Сложить их в один файл значило бы однажды переиспользовать хук не там.
//
// Инженеры продолжают работать на портале 73: раздел в CRM — обзор. Ни одной записи здесь
// нет и появиться не должно.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/p73x'

/** Тот же разбор ответа, что в api/client.ts и api/p73.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра здесь — это чаще всего «не назван человек и не запрошен свод».
    // Пустая таблица читалась бы как «переработок не было», а это ложь про зарплату.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p73x_api.py) ───────────

export interface Мостик {
  d1_id: number | null
  bitrix_id: number | null
}

export interface СудноP73X extends Мостик {
  name: string | null
  imo: string | null
}

export interface ЗаявкаP73X extends Мостик {
  title: string | null
  status: string | null
  location: string | null
}

export interface ИнженерP73X extends Мостик {
  name: string | null
}

interface Мостики {
  engineer: ИнженерP73X | null
  deal: ЗаявкаP73X | null
  vessel: СудноP73X | null
}

/** Одна запись овертайма. Деньги — sum_rub; они есть у каждой строки. */
export interface ОвертаймСтрока extends Мостики {
  d1_id: number
  driver_name: string | null
  region: string | null
  charge_region: string | null
  category: string | null
  work_date: string | null
  day_type: string | null
  work_hours: number | null
  road_hours: number | null
  amort_km: number | null
  duty: number | null
  raid: number | null
  trip_rub: number | null
  trip_work_days: number | null
  trip_wait_days: number | null
  remote_port: string | null
  month: string | null
  sum_rub: number | null
  approved: boolean | null
  approved_at: string | null
  pay_status: string | null
  batch_id: number | null
  batch_title: string | null
  batch_status: string | null
  flagged: boolean | null
  flag_reason: string | null
  comment: string | null
  avr: string | null
  deal_raw: string | null
  created_at: string | null
}

export interface ОвертаймИтоги {
  n: number | null
  work_hours: number | null
  road_hours: number | null
  sum_rub: number | null
  approved_n: number | null
  paid_n: number | null
  flagged_n: number | null
  people: number | null
}

export interface СводСтрока {
  driver_name: string | null
  region: string | null
  eng_bitrix_id: number | null
  eng_d1_id: number | null
  n: number
  work_hours: number | null
  road_hours: number | null
  amort_km: number | null
  sum_rub: number | null
  approved_n: number
  paid_n: number
  unpaid_rub: number | null
  first_day: string | null
  last_day: string | null
}

export interface КомандировкаСтрока extends Мостики {
  d1_id: number
  engineer_name: string | null
  destination: string | null
  purpose: string | null
  /** 'ru' | 'foreign' — от этого ставка суточных. */
  kind: string | null
  /** 'engineer' — поездка, 'ds' — деньги под отчёт без поездки. Складывать нельзя. */
  trip_kind: string | null
  vessel_name: string | null
  date_from: string | null
  date_to: string | null
  days: number | null
  perdiem_rate: number | null
  perdiem_total: number | null
  advance: number | null
  advance_requested: number | null
  expenses: { cat?: string; label?: string; amount?: number }[] | null
  status: string | null
  order_no: string | null
  bitrix_element_id: number | null
  created_at: string | null
  updated_at: string | null
  deal_raw: string | null
}

export interface ПоездкаСтрока extends Мостики {
  d1_id: number
  driver_name: string | null
  region: string | null
  purpose: string | null
  destination: string | null
  date_out: string | null
  date_back: string | null
  km: number | null
  route_km: number | null
  status: string | null
  flagged: boolean | null
  return_issues: string | null
  fuel_cash: boolean | null
  fuel_cash_amount: number | null
  car_d1_id: number | null
  car_plate: string | null
  car_title: string | null
  car_kind: string | null
  deal_raw: string | null
}

export interface ЧеловекP73X {
  name: string
  d1_id: number | null
  bitrix_id: number | null
  n: number
  /** У записи нет учётки портала: её завели за человека. */
  no_account: boolean
}

export interface СвежестьP73X {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface СправочникиP73X {
  months: Record<string, number>
  regions: Record<string, number>
  categories: Record<string, number>
  pay_status: Record<string, number>
  trip_kind: Record<string, number>
  trip_status: Record<string, number>
  trip_regions: Record<string, number>
  people: ЧеловекP73X[]
  counts: {
    overtime: number
    batches: number
    errors_open: number
    trips: number
    trips_real: number
    car_trips: number
    car_trips_open: number
    cars: number
    refuels: number
    taxi: number
    overtime_from: string | null
    overtime_to: string | null
  }
  freshness: СвежестьP73X[]
  data_notes: Record<string, string>
}

/** Общая обёртка списка ядра + пометка «здесь деньги нескольких людей». */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  restricted_why?: string
}

export interface ФильтрыОвертаймов {
  /** Индекс нужен, чтобы фильтры можно было передать в строкаЗапроса() как обычный набор
   *  пар — иначе TypeScript отказывается видеть в именованном типе Record. */
  [ключ: string]: unknown
  month?: string
  region?: string
  category?: string
  pay?: string
  approved?: string
  flagged?: string
  engineer?: number | string
  engineer_bx?: number | string
  all?: boolean
  from?: string
  to?: string
  q?: string
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useP73XRefs() {
  return useQuery<СправочникиP73X>({
    queryKey: ['p73x', 'refs'],
    queryFn: () => запрос<СправочникиP73X>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useОвертаймы(ф: ФильтрыОвертаймов) {
  return useQuery<Список<ОвертаймСтрока> & { totals: ОвертаймИтоги }>({
    queryKey: ['p73x', 'overtime', ф],
    queryFn: () => запрос('/overtime' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useСводОвертаймов(ф: ФильтрыОвертаймов) {
  return useQuery<Список<СводСтрока>>({
    queryKey: ['p73x', 'overtime-summary', ф],
    queryFn: () => запрос('/overtime-summary' + строкаЗапроса({ ...ф, all: true })),
    ...ОБЩЕЕ,
  })
}

export function useКомандировки(ф: Record<string, unknown>) {
  return useQuery<Список<КомандировкаСтрока>>({
    queryKey: ['p73x', 'trips', ф],
    queryFn: () => запрос('/trips' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useПоездки(ф: Record<string, unknown>) {
  return useQuery<Список<ПоездкаСтрока> & { totals: Record<string, number | null> }>({
    queryKey: ['p73x', 'car-trips', ф],
    queryFn: () => запрос('/car-trips' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}
